import { useTranslation } from "react-i18next";
import {
  DocumentText1,
  Calendar,
  Briefcase,
  CalendarTick,
  Clock,
  Coffee,
  Building,
  Money,
  Timer,
} from "iconsax-react";
import { formatDateForDisplay, formatMoneyKzt, type Locale } from "@/shared/utils";
import type { WorkerDetailResponse } from "../types";
import InfoRow from "./InfoRow";

interface Props {
  worker: WorkerDetailResponse;
}

function trimSeconds(time: string): string {
  return time.replace(/:\d{2}$/, "");
}

export default function ContractTab({ worker }: Props) {
  const { t, i18n } = useTranslation("WorkerDetailPage");
  const locale = (i18n.language as Locale) || "ru";
  const contract = worker.active_contract;

  if (!contract) {
    return (
      <div className="p-7 radius-3xl surface-base-fill flex items-center justify-center min-h-[200px]">
        <p className="text-body-regular-md content-base-secondary">{t("contract.noContract")}</p>
      </div>
    );
  }

  function formatWorkTime(): string {
    if (!contract?.work_start_time || !contract?.work_end_time) return "";
    return `${trimSeconds(contract.work_start_time)} — ${trimSeconds(contract.work_end_time)}`;
  }

  function formatBreakTime(): string {
    if (!contract?.has_break) return t("contract.schedule.noBreak");
    if (!contract?.break_start_time || !contract?.break_end_time) return "";
    return `${trimSeconds(contract.break_start_time)} — ${trimSeconds(contract.break_end_time)}`;
  }

  function formatSalary(): string {
    if (!contract?.salary_amount) return "";
    return formatMoneyKzt(contract.salary_amount, locale);
  }

  function formatWorkingDays(): string {
    if (!contract?.working_days_list || contract.working_days_list.length === 0) return "";
    return contract.working_days_list.map((day) => t(`weekdays.${day}`)).join(", ");
  }

  function formatJobPosition(): string {
    if (locale === "kk" && contract?.job_position_kk) {
      return contract.job_position_kk;
    }
    return contract?.job_position_ru ?? "";
  }

  function renderTrialPeriod(): string {
    if (!contract?.trial_period) return t("personal.no");
    if (contract.trial_duration_months) {
      return t("contract.schedule.trialMonths", { count: contract.trial_duration_months });
    }
    return t("personal.yes");
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="p-7 radius-3xl surface-base-fill flex flex-col gap-4">
        <h2 className="text-body-bold-lg content-base-primary pb-3 border-b surface-app-stroke">{t("contract.title")}</h2>
        <div className="flex flex-col">
          <InfoRow icon={DocumentText1} label={t("contract.contractNumber")} value={contract.contract_number} />
          <InfoRow icon={Calendar} label={t("contract.startDate")} value={formatDateForDisplay(contract.start_date)} />
          <InfoRow icon={Briefcase} label={t("contract.jobPosition")} value={formatJobPosition()} />
        </div>
      </div>

      <div className="p-7 radius-3xl surface-base-fill flex flex-col gap-4">
        <h2 className="text-body-bold-lg content-base-primary pb-3 border-b surface-app-stroke">{t("contract.schedule.title")}</h2>
        <div className="flex flex-col">
          <InfoRow icon={CalendarTick} label={t("contract.schedule.workingDays")} value={formatWorkingDays()} />
          <InfoRow icon={Clock} label={t("contract.schedule.workTime")} value={formatWorkTime()} />
          <InfoRow icon={Coffee} label={t("contract.schedule.breakTime")} value={formatBreakTime()} />
          <InfoRow
            icon={Building}
            label={t("contract.schedule.workFormat")}
            value={t(`contract.schedule.workFormatValues.${contract.work_format}`)}
          />
          <InfoRow icon={Money} label={t("contract.schedule.salary")} value={formatSalary()} />
          <InfoRow icon={Timer} label={t("contract.schedule.trialPeriod")} value={renderTrialPeriod()} />
        </div>
      </div>
    </div>
  );
}
